<script setup lang="ts">
import { computed, watch, watchEffect, onMounted, onUnmounted } from 'vue'
import GeneralDisclaimer from '~/components/common/GeneralDisclaimer.vue'
import { generateSalaryCalculatorPageMeta } from '~/utils/meta/generateSalaryCalculatorPageMeta'

definePageMeta({
  layout: 'marketing'
})

// Interfaces
interface Jurisdiction {
  code: string
  name: string
  type: string
}

interface JobTitle {
  title: string
  socCode: string
  avgSalary: number
  dataPoints: number
}

interface SalaryData {
  id: string
  jobTitle: string
  metropolitanArea: string
  currency: string
  payPeriod: string
  P10: number
  P25: number
  P50: number
  P75: number
  P90: number
}

interface CityLocation {
  label: string
  value: string
  city: string
  state?: string
  state_abr: string
  dataPoints?: number
}

interface TopPayingJob {
  jobTitle: string
  avgSalary: number
  P10: number
  P25: number
  P50: number
  P75: number
  P90: number
  dataPoints: number
  logo: string | null
  company_name: string | null
  company_permalink: string | null
}

interface LocationJob {
  job_id: string
  title: string
  company_name: string
  company_permalink: string
  logo: string | null
  city: string
  state: string
  type: string | null
  salary_min: number | null
  salary_max: number | null
  published_at: string | null
}

// Router and route for URL management
const route = useRoute()
const router = useRouter()

// US State name to code mapping
const stateNameToCode: Record<string, string> = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
  'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
  'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
  'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
  'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new-hampshire': 'NH', 'new-jersey': 'NJ',
  'new-mexico': 'NM', 'new-york': 'NY', 'north-carolina': 'NC', 'north-dakota': 'ND', 'ohio': 'OH',
  'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode-island': 'RI', 'south-carolina': 'SC',
  'south-dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
  'virginia': 'VA', 'washington': 'WA', 'west-virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
  'district-of-columbia': 'DC', 'puerto-rico': 'PR'
}

const stateCodeToName: Record<string, string> = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  'DC': 'District of Columbia', 'PR': 'Puerto Rico'
}

// All states for internal linking (sorted alphabetically)
const allStates = Object.entries(stateCodeToName)
  .map(([code, name]) => ({
    code,
    name,
    slug: name.toLowerCase().replace(/\s+/g, '-')
  }))
  .sort((a, b) => a.name.localeCompare(b.name))

// Slug utilities
const toSlug = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

const fromSlug = (slug: string): string => {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Build URL path for a metropolitan area (e.g., "Nashville, TN" -> "/salary-calculator/nashville-tn")
const buildMetroUrl = (metropolitanArea: string, role?: string | null): string => {
  if (!metropolitanArea) return '/salary-calculator'
  const metroMatch = metropolitanArea.match(/^(.+),\s*([A-Z]{2})$/i)
  if (!metroMatch) return '/salary-calculator'

  const city = metroMatch[1].trim()
  const stateCode = metroMatch[2].toUpperCase()
  const citySlug = toSlug(city)

  let path = `/salary-calculator/${citySlug}-${stateCode.toLowerCase()}`

  if (role) {
    path += `/${toSlug(role)}`
  }

  return path
}

// Build URL path for a specific role with current location context
const buildRoleUrl = (role: string): string => {
  let path = '/salary-calculator'

  // Add location first (if any) based on displayed values
  if (displayedMetro.value) {
    const metroMatch = displayedMetro.value.match(/^(.+),\s*([A-Z]{2})/)
    if (metroMatch) {
      const citySlug = toSlug(metroMatch[1])
      const stateCode = metroMatch[2].toLowerCase()
      path += `/${citySlug}-${stateCode}`
    }
  } else if (displayedState.value) {
    const stateName = stateCodeToName[displayedState.value]
    if (stateName) {
      path += `/${toSlug(stateName)}`
    }
  }

  // Add role
  if (role) {
    path += `/${toSlug(role)}`
  }

  return path
}

// Parse URL path to extract location and role
// URL format: /salary-calculator/{location}/{role}
// - /salary-calculator/los-angeles-ca/pharmacist (city-state + role)
// - /salary-calculator/california/pharmacist (state + role)
// - /salary-calculator/pharmacist (role only)
const parseSlug = () => {
  const slugParts = route.params.slug as string[] | undefined
  let role: string | null = null
  let stateCode: string | null = null
  let metro: string | null = null

  if (!slugParts || slugParts.length === 0) {
    return { role, stateCode, metro }
  }

  // Check if first part is a location or role
  const firstPart = slugParts[0].toLowerCase()

  // Check if it's a city-state pattern (e.g., "los-angeles-ca")
  const cityStateMatch = firstPart.match(/^(.+)-([a-z]{2})$/)

  if (cityStateMatch && stateCodeToName[cityStateMatch[2].toUpperCase()]) {
    // First part is city-state location
    const citySlug = cityStateMatch[1]
    const stateCodeFromUrl = cityStateMatch[2].toUpperCase()
    stateCode = stateCodeFromUrl
    const cityName = fromSlug(citySlug)
    metro = `${cityName}, ${stateCode}`

    // Second part is the role
    if (slugParts[1]) {
      role = fromSlug(slugParts[1])
    }
  } else if (stateNameToCode[firstPart]) {
    // First part is a state name
    stateCode = stateNameToCode[firstPart]

    // Second part is the role
    if (slugParts[1]) {
      role = fromSlug(slugParts[1])
    }
  } else {
    // First part is the role (no location)
    role = fromSlug(slugParts[0])
  }

  return { role, stateCode, metro }
}

// Parse initial values from URL
const parsedParams = parseSlug()

// Unique key prefix for useAsyncData based on route (prevents cache conflicts)
const routeKey = (route.params.slug as string[] || []).join('-') || 'home'

// Build slug for indexable check
// - For city+title pages: "city-state/role" like "nashville-tn/cashier"
// - For state pages: state slug like "kansas"
const buildIndexableSlug = (): string | null => {
  const { metro, role, stateCode } = parsedParams

  // City+title page
  if (metro && role) {
    const metroMatch = metro.match(/^(.+),\s*([A-Z]{2})$/i)
    if (!metroMatch) return null
    const citySlug = toSlug(metroMatch[1])
    const stateCodeLower = metroMatch[2].toLowerCase()
    const roleSlug = toSlug(role)
    return `${citySlug}-${stateCodeLower}/${roleSlug}`
  }

  // State-only page (no metro, no role, just stateCode)
  if (stateCode && !metro && !role) {
    const stateName = stateCodeToName[stateCode]
    if (stateName) {
      return toSlug(stateName)
    }
  }

  return null
}

// Check if current page should be indexed (state pages or city+title with search volume)
const initialSlug = buildIndexableSlug()
const { data: indexableData } = await useAsyncData(
  `indexable-${routeKey}-${initialSlug || 'none'}`,
  async () => {
    if (!initialSlug) return { indexable: false }
    return await $fetch<{ indexable: boolean }>('/api/tax/is-indexable', {
      query: { slug: initialSlug }
    })
  },
  { default: () => ({ indexable: false }) }
)

// Fetch all indexed slugs for related jobs links
const { data: indexedSlugsData } = await useAsyncData(
  'indexed-slugs',
  () => $fetch<{ slugs: string[] }>('/api/tax/indexed-slugs'),
  { default: () => ({ slugs: [] }) }
)
const indexedSlugsSet = computed(() => new Set(indexedSlugsData.value?.slugs || []))

// Check if a job title is indexed (has a page) for the current location
const isJobIndexed = (jobTitle: string): boolean => {
  if (!displayedMetro.value) return false // Only city+title pages are indexed

  const metroMatch = displayedMetro.value.match(/^(.+),\s*([A-Z]{2})/)
  if (!metroMatch) return false

  const citySlug = toSlug(metroMatch[1])
  const stateCode = metroMatch[2].toLowerCase()
  const roleSlug = toSlug(jobTitle)
  const slug = `${citySlug}-${stateCode}/${roleSlug}`

  return indexedSlugsSet.value.has(slug)
}

// Form state - updates when user changes inputs
const selectedState = ref<string | null>(parsedParams.stateCode)
const selectedMetro = ref<string | null>(parsedParams.metro)
const selectedRole = ref<string | null>(parsedParams.role)
const roleSearchQuery = ref(parsedParams.role || '')
const metroSearchQuery = ref(parsedParams.metro ? parsedParams.metro.split(',')[0] : '')

// Display state - only updates when Calculate is clicked (used for showing results)
// These are the values that were used to fetch the current data
const displayedState = ref<string | null>(parsedParams.stateCode)
const displayedMetro = ref<string | null>(parsedParams.metro)
const displayedRole = ref<string | null>(parsedParams.role)
const displayedSocCode = ref<string | null>(null)

const isLoadingRoles = ref(false)
const isLoadingCities = ref(false)

const citySearchResults = ref<CityLocation[]>([])
const searchResults = ref<JobTitle[]>([])
const selectedJobData = ref<JobTitle | null>(null)

// Fetch SOC code for the role from URL (for SSR consistency)
const { data: initialRoleData } = await useAsyncData(
  `initialRoleData-${routeKey}`,
  async () => {
    if (!parsedParams.role) return null

    // Fetch the role data to get SOC code
    const response = await $fetch<{ success: boolean; data: JobTitle[] }>('/api/tax/job-titles', {
      query: { q: parsedParams.role, limit: 1 }
    })

    if (response.success && response.data.length > 0) {
      // Find exact match or best match
      const exactMatch = response.data.find(
        job => job.title.toLowerCase() === parsedParams.role?.toLowerCase()
      )
      return exactMatch || response.data[0]
    }
    return null
  },
  { default: () => null }
)

// Initialize displayedSocCode from URL role lookup
if (initialRoleData.value?.socCode) {
  displayedSocCode.value = initialRoleData.value.socCode
  selectedJobData.value = initialRoleData.value
}

// Dropdowns visibility
const showStateDropdown = ref(false)
const showMetroDropdown = ref(false)
const showRoleDropdown = ref(false)

// Currency formatting
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount)
}

// Build URL path from current selections
// URL format: /salary-calculator/{location}/{role}
const buildUrlPath = (): string => {
  let path = '/salary-calculator'

  // Add location first (if any)
  if (selectedMetro.value) {
    // Extract city and state from metro (e.g., "Dallas, TX" -> "dallas-tx")
    const metroMatch = selectedMetro.value.match(/^(.+),\s*([A-Z]{2})/)
    if (metroMatch) {
      const citySlug = toSlug(metroMatch[1])
      const stateCode = metroMatch[2].toLowerCase()
      path += `/${citySlug}-${stateCode}`
    }
  } else if (selectedState.value) {
    // Use state name slug (e.g., "TX" -> "texas")
    const stateName = stateCodeToName[selectedState.value]
    if (stateName) {
      path += `/${toSlug(stateName)}`
    }
  }

  // Add role (if any)
  if (selectedRole.value) {
    path += `/${toSlug(selectedRole.value)}`
  }

  return path
}

// Update URL (called when Calculate button is clicked)
const updateUrl = () => {
  const newPath = buildUrlPath()
  if (route.path !== newPath) {
    router.replace(newPath)
  }
}

// SSR Data Fetching - Jurisdictions
const { data: jurisdictionsData } = await useAsyncData(
  `jurisdictions-${routeKey}`,
  () => $fetch<{ success: boolean; data: Jurisdiction[] }>('/api/tax/jurisdictions?country=US'),
  { default: () => ({ success: false, data: [] }) }
)
const jurisdictions = computed(() => jurisdictionsData.value?.data || [])

// City search handler (uses salary-locations API to get locations with salary data)
let citySearchTimeout: NodeJS.Timeout | null = null
const fetchCities = async (query?: string) => {
  if (!selectedState.value) return

  isLoadingCities.value = true
  try {
    const response = await $fetch<{ success: boolean; data: CityLocation[] }>('/api/tax/salary-locations', {
      query: { q: query || '', state: selectedState.value, limit: 20 }
    })
    if (response.success) {
      citySearchResults.value = response.data
      showMetroDropdown.value = citySearchResults.value.length > 0
    }
  } catch (error) {
    console.error('Error searching cities:', error)
    citySearchResults.value = []
  } finally {
    isLoadingCities.value = false
  }
}

const handleCitySearch = () => {
  if (citySearchTimeout) clearTimeout(citySearchTimeout)

  if (!selectedState.value) {
    citySearchResults.value = []
    showMetroDropdown.value = false
    return
  }

  if (!metroSearchQuery.value || metroSearchQuery.value.length < 2) {
    // Fetch popular/default cities when input is empty or too short
    fetchCities()
    return
  }

  citySearchTimeout = setTimeout(() => fetchCities(metroSearchQuery.value), 300)
}

const handleMetroFocus = () => {
  if (!selectedState.value) return

  // Always fetch cities on focus to ensure fresh data
  fetchCities(metroSearchQuery.value || '')
}

// SSR Data fetching - Salary Data (fetches on page load if role is in URL)
// Uses displayedRole/displayedState/displayedMetro for consistent results display
const { data: salaryDataResponse, status: salaryStatus } = await useAsyncData(
  `salaryData-${routeKey}`,
  async () => {
    if (!displayedRole.value) return { success: true, data: [] }

    const salaryQuery: Record<string, string> = {
      job_title: displayedRole.value,
      limit: '20'
    }
    if (displayedState.value) salaryQuery.state = displayedState.value
    if (displayedMetro.value) salaryQuery.metro_area = displayedMetro.value

    return $fetch<{ success: boolean; data: SalaryData[] }>('/api/tax/salary-data', {
      query: salaryQuery
    })
  },
  { default: () => ({ success: true, data: [] }) }
)
const salaryData = computed(() => salaryDataResponse.value?.data || [])
const isLoadingSalary = computed(() => salaryStatus.value === 'pending')

// SSR Data fetching - National Salary Data (for comparison across US)
const { data: nationalSalaryResponse } = await useAsyncData(
  `nationalSalaryData-${routeKey}`,
  async () => {
    // Fetch national data when we have a role OR a location selected
    if (!displayedRole.value && !displayedState.value && !displayedMetro.value) {
      return { success: true, data: [] }
    }

    const query: Record<string, string> = {}
    if (displayedRole.value) {
      query.job_title = displayedRole.value
    }

    return $fetch<{ success: boolean; data: SalaryData[] }>('/api/tax/salary-data', { query })
  },
  { default: () => ({ success: true, data: [] }) }
)
const nationalSalaryData = computed(() => nationalSalaryResponse.value?.data || [])

// SSR Data fetching - State Salary Data (for comparison at state level)
const { data: stateSalaryResponse } = await useAsyncData(
  `stateSalaryData-${routeKey}`,
  async () => {
    // Fetch state data when we have a state selected (with or without role)
    if (!displayedState.value) return { success: true, data: [] }

    const query: Record<string, string> = {
      state: displayedState.value,
      limit: '50'
    }
    if (displayedRole.value) {
      query.job_title = displayedRole.value
    }

    return $fetch<{ success: boolean; data: SalaryData[] }>('/api/tax/salary-data', { query })
  },
  { default: () => ({ success: true, data: [] }) }
)
const stateSalaryData = computed(() => stateSalaryResponse.value?.data || [])

// SSR Data fetching - Location Salary Stats (fetches when only location is selected, no role)
// Returns aggregated salary data for all jobs in the location
const { data: locationSalaryResponse, status: locationSalaryStatus } = await useAsyncData(
  `locationSalaryData-${routeKey}`,
  async () => {
    // Only fetch if we have a location but no role
    if (displayedRole.value || (!displayedState.value && !displayedMetro.value)) {
      return { success: true, data: [] }
    }

    const salaryQuery: Record<string, string> = {
      limit: '100' // Get more data points for better aggregation
    }
    if (displayedState.value) salaryQuery.state = displayedState.value
    if (displayedMetro.value) salaryQuery.metro_area = displayedMetro.value

    return $fetch<{ success: boolean; data: SalaryData[] }>('/api/tax/salary-data', {
      query: salaryQuery
    })
  },
  { default: () => ({ success: true, data: [] }) }
)
const locationSalaryData = computed(() => locationSalaryResponse.value?.data || [])
const isLoadingLocationSalary = computed(() => locationSalaryStatus.value === 'pending')

// SSR Data fetching - Top Paying Jobs / Related Jobs
// When a role is selected, fetches related jobs based on SOC code prefix (optionally filtered by location)
// SOC code format: XX-XXXX (e.g., 29-1051)
// - First 2 digits (XX): Major group (e.g., 29 = Healthcare)
// - First 4 chars (XX-X): Minor group
// - First 5 chars (XX-XX): Detailed group
// When only location is selected, fetches top paying jobs for that location
const { data: topPayingJobsResponse, status: topJobsStatus } = await useAsyncData(
  `topPayingJobs-${routeKey}`,
  async () => {
    const hasLocation = displayedState.value || displayedMetro.value
    const hasRole = displayedSocCode.value

    // Need either a location OR a SOC code to fetch jobs
    if (!hasLocation && !hasRole) {
      return { success: true, data: [] }
    }

    const topJobsQuery: Record<string, string> = { limit: '50' }

    // Add location filters if location is selected
    if (displayedMetro.value) {
      topJobsQuery.metro = displayedMetro.value
    } else if (displayedState.value) {
      topJobsQuery.state = displayedState.value
    }

    // Use SOC prefix filter when a role is selected (shows related jobs in same category)
    if (hasRole) {
      // Try progressively broader prefixes: XX-XX (5 chars) -> XX-X (4 chars) -> XX (2 chars)
      // Start with minor group (first 5 chars like "29-10") for more specific related jobs
      const socCode = displayedSocCode.value!
      // Use first 2 digits for major occupation group (broad category)
      const socPrefix = socCode.substring(0, 2)
      topJobsQuery.soc_prefix = socPrefix
    }

    return $fetch<{ success: boolean; data: TopPayingJob[] }>('/api/tax/top-paying-jobs', {
      query: topJobsQuery
    })
  },
  { default: () => ({ success: true, data: [] }) }
)
const topPayingJobs = computed(() => topPayingJobsResponse.value?.data || [])
const isLoadingTopJobs = computed(() => topJobsStatus.value === 'pending')

// SSR Data fetching - Jobs in location (from job_indexation)
const { data: locationJobsResponse, status: locationJobsStatus } = await useAsyncData(
  `locationJobs-${routeKey}`,
  async () => {
    const hasLocation = displayedState.value || displayedMetro.value

    // Only fetch if we have a location
    if (!hasLocation) {
      return { success: true, data: { jobs: [] } }
    }

    const query: Record<string, string> = { limit: '8' }

    if (displayedMetro.value) {
      query.metro = displayedMetro.value
    } else if (displayedState.value) {
      query.state = displayedState.value
    }

    return $fetch<{ success: boolean; data: { jobs: LocationJob[] } }>('/api/jobs/by-location', {
      query
    })
  },
  { default: () => ({ success: true, data: { jobs: [] } }) }
)
const locationJobs = computed(() => locationJobsResponse.value?.data?.jobs || [])
const isLoadingLocationJobs = computed(() => locationJobsStatus.value === 'pending')

// Format job salary for display
const formatJobSalary = (job: LocationJob): string => {
  if (job.salary_min && job.salary_max) {
    return `$${(job.salary_min / 1000).toFixed(0)}k - $${(job.salary_max / 1000).toFixed(0)}k`
  }
  if (job.salary_min) {
    return `From $${(job.salary_min / 1000).toFixed(0)}k`
  }
  if (job.salary_max) {
    return `Up to $${(job.salary_max / 1000).toFixed(0)}k`
  }
  return ''
}

// Format time ago for job posting date
const formatTimeAgo = (dateString: string | null): string => {
  if (!dateString) return 'Recently'
  const date = new Date(dateString)
  const now = new Date()
  const diffInMs = now.getTime() - date.getTime()
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
  if (diffInDays === 0) return 'Today'
  if (diffInDays === 1) return 'Yesterday'
  if (diffInDays < 7) return `${diffInDays} days ago`
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`
  return `${Math.floor(diffInDays / 30)} months ago`
}

// Filter jobs with similar salaries and take top 10 unique titles
const uniqueTopPayingJobs = computed(() => {
  if (!topPayingJobs.value.length) return []

  const result: TopPayingJob[] = []

  for (const job of topPayingJobs.value) {
    // Check if this job's salary is too similar to any job we've already added
    // Consider similar if within 1% of an existing salary
    const isSimilar = result.some(existingJob => {
      const threshold = existingJob.avgSalary * 0.01 // 1% threshold
      return Math.abs(existingJob.avgSalary - job.avgSalary) < threshold
    })

    if (!isSimilar) {
      result.push(job)
    }

    // Stop once we have 10 unique jobs
    if (result.length >= 10) break
  }

  return result
})

// Title for related/top paying jobs section
const relatedJobsTitle = computed(() => {
  const hasLocation = displayedState.value || displayedMetro.value
  // Show "Related Jobs" when a role is selected
  if (displayedRole.value && displayedSocCode.value) {
    if (hasLocation) {
      return `Related Jobs in ${locationDisplayName.value}`
    }
    return 'Related Jobs'
  }
  return `Best Paying Jobs in ${locationDisplayName.value}`
})

// FAQ items based on extracted data
const faqItems = computed(() => {
  const items: { question: string; answer: string }[] = []
  const hasLocation = displayedState.value || displayedMetro.value
  const location = locationDisplayName.value

  // Role-specific FAQs
  if (displayedRole.value && aggregatedSalary.value) {
    items.push({
      question: `What is the average salary for a ${displayedRole.value}${hasLocation ? ' in ' + location : ''}?`,
      answer: `The median annual salary for a ${displayedRole.value}${hasLocation ? ' in ' + location : ''} is ${formatCurrency(aggregatedSalary.value.P50)}. Salaries typically range from ${formatCurrency(aggregatedSalary.value.P10)} at the entry level (10th percentile) to ${formatCurrency(aggregatedSalary.value.P90)} for top earners (90th percentile).`
    })

    items.push({
      question: `What is the salary range for ${displayedRole.value}${hasLocation ? ' in ' + location : ''}?`,
      answer: `${displayedRole.value} salaries${hasLocation ? ' in ' + location : ''} range from ${formatCurrency(aggregatedSalary.value.P10)} to ${formatCurrency(aggregatedSalary.value.P90)}. The 25th percentile earns ${formatCurrency(aggregatedSalary.value.P25)}, while the 75th percentile earns ${formatCurrency(aggregatedSalary.value.P75)}.`
    })

    items.push({
      question: `How much does a ${displayedRole.value} make per month${hasLocation ? ' in ' + location : ''}?`,
      answer: `Based on the median annual salary of ${formatCurrency(aggregatedSalary.value.P50)}, a ${displayedRole.value}${hasLocation ? ' in ' + location : ''} makes approximately ${formatCurrency(Math.round(aggregatedSalary.value.P50 / 12))} per month before taxes.`
    })
  }

  // Location-specific FAQs (when location is selected)
  if (hasLocation && aggregatedLocationSalary.value) {
    items.push({
      question: `What is the average salary in ${location}?`,
      answer: `The median annual salary in ${location} is ${formatCurrency(aggregatedLocationSalary.value.P50)}. Salaries range from ${formatCurrency(aggregatedLocationSalary.value.P10)} to ${formatCurrency(aggregatedLocationSalary.value.P90)} depending on the occupation and experience level.`
    })
  }

  // Related jobs FAQ
  if (uniqueTopPayingJobs.value.length > 0 && displayedRole.value) {
    const topJob = uniqueTopPayingJobs.value[0]
    const relatedJobsList = uniqueTopPayingJobs.value.slice(0, 3).map(j => j.jobTitle).join(', ')
    items.push({
      question: `What jobs are similar to ${displayedRole.value}?`,
      answer: `Related occupations include ${relatedJobsList}. The highest paying related job is ${topJob.jobTitle} with a median salary of ${formatCurrency(topJob.avgSalary)}.`
    })
  }

  // Top paying jobs FAQ (location only)
  if (hasLocation && uniqueTopPayingJobs.value.length > 0 && !displayedRole.value) {
    const topJob = uniqueTopPayingJobs.value[0]
    items.push({
      question: `What is the highest paying job in ${location}?`,
      answer: `The highest paying job in ${location} is ${topJob.jobTitle} with a median salary of ${formatCurrency(topJob.avgSalary)}. Salaries for this role range from ${formatCurrency(topJob.P10)} to ${formatCurrency(topJob.P90)}.`
    })
  }

  return items
})

// Handle Calculate button click - full page navigation (same behavior as refresh)
const handleCalculate = () => {
  // Need at least a role OR a location to calculate
  if (!selectedRole.value && !selectedState.value && !selectedMetro.value) return

  // Build URL and do a full page reload (not client-side navigation)
  const path = buildUrlPath()
  window.location.href = path
}

// Search for job titles (client-side only for interactive search)
let searchTimeout: NodeJS.Timeout | null = null
const fetchJobTitles = async (query?: string) => {
  isLoadingRoles.value = true
  try {
    const response = await $fetch<{ success: boolean; data: JobTitle[] }>('/api/tax/job-titles', {
      query: { q: query || '', limit: 20 }
    })
    if (response.success) {
      searchResults.value = response.data
      showRoleDropdown.value = true
    }
  } catch (error) {
    console.error('Error searching jobs:', error)
  } finally {
    isLoadingRoles.value = false
  }
}

const handleRoleSearch = () => {
  if (searchTimeout) clearTimeout(searchTimeout)

  if (!roleSearchQuery.value || roleSearchQuery.value.length < 2) {
    // Fetch popular/default job titles when input is empty or too short
    fetchJobTitles()
    return
  }

  searchTimeout = setTimeout(() => fetchJobTitles(roleSearchQuery.value), 300)
}

const handleRoleFocus = () => {
  if (searchResults.value.length > 0) {
    showRoleDropdown.value = true
  } else {
    // Fetch popular job titles on focus if no results yet
    fetchJobTitles(roleSearchQuery.value || '')
  }
}

// Selection handlers
const selectState = (jurisdiction: Jurisdiction) => {
  selectedState.value = jurisdiction.code
  selectedMetro.value = null
  metroSearchQuery.value = ''
  showStateDropdown.value = false
}

const selectMetro = (city: CityLocation) => {
  // Store as "City, ST" format for compatibility
  selectedMetro.value = `${city.city}, ${city.state_abr}`
  metroSearchQuery.value = city.city || ''
  showMetroDropdown.value = false
}

const selectRole = (job: JobTitle) => {
  selectedRole.value = job.title
  selectedJobData.value = job
  roleSearchQuery.value = job.title
  showRoleDropdown.value = false
}

const clearState = () => {
  selectedState.value = null
  selectedMetro.value = null
  metroSearchQuery.value = ''
}

const clearMetro = () => {
  selectedMetro.value = null
  metroSearchQuery.value = ''
}

const clearRole = () => {
  selectedRole.value = null
  selectedJobData.value = null
  roleSearchQuery.value = ''
}

// Computed values for form display
const selectedStateName = computed(() => {
  if (!selectedState.value) return null
  return jurisdictions.value.find(j => j.code === selectedState.value)?.name || selectedState.value
})

// Computed values for results display (uses displayed state, not form state)
const displayedStateName = computed(() => {
  if (!displayedState.value) return null
  return jurisdictions.value.find(j => j.code === displayedState.value)?.name || displayedState.value
})

// City slug for related salary pages (e.g., "Dallas, TX" -> "dallas-tx")
const citySlugForRelated = computed(() => {
  if (!displayedMetro.value) return null
  const metroMatch = displayedMetro.value.match(/^(.+),\s*([A-Z]{2})$/i)
  if (!metroMatch) return null
  const citySlug = toSlug(metroMatch[1])
  const stateCode = metroMatch[2].toLowerCase()
  return `${citySlug}-${stateCode}`
})

// Current page slug for excluding from related pages (e.g., "dallas-tx/cashier")
const currentPageSlug = computed(() => {
  if (!citySlugForRelated.value || !displayedRole.value) return null
  return `${citySlugForRelated.value}/${toSlug(displayedRole.value)}`
})

// State slug for tax calculator links (e.g., "California" -> "california")
const displayedStateSlug = computed(() => {
  if (!displayedStateName.value) return null
  return toSlug(displayedStateName.value)
})

// Current median salary for tax calculator links
const currentMedianSalary = computed(() => {
  if (aggregatedSalary.value?.P50) return aggregatedSalary.value.P50
  if (aggregatedLocationSalary.value?.P50) return aggregatedLocationSalary.value.P50
  return null
})

// City search results filtered to exclude undefined/empty city names
const filteredCities = computed(() =>
  citySearchResults.value.filter(city => city.city && city.label && city.city !== 'undefined')
)

// hasResults uses displayedRole so results only show after Calculate is clicked
const hasResults = computed(() => displayedRole.value && salaryData.value.length > 0)

// hasLocationOnlyResults - show top paying jobs when only location is selected (no role)
const hasLocationOnlyResults = computed(() =>
  !displayedRole.value && (displayedState.value || displayedMetro.value) && (uniqueTopPayingJobs.value.length > 0 || locationSalaryData.value.length > 0)
)

// Aggregated salary stats for location-only view (all jobs in the location)
const aggregatedLocationSalary = computed(() => {
  if (!locationSalaryData.value.length) return null

  const count = locationSalaryData.value.length
  const avgP10 = Math.round(locationSalaryData.value.reduce((sum, d) => sum + d.P10, 0) / count)
  const avgP25 = Math.round(locationSalaryData.value.reduce((sum, d) => sum + d.P25, 0) / count)
  const avgP50 = Math.round(locationSalaryData.value.reduce((sum, d) => sum + d.P50, 0) / count)
  const avgP75 = Math.round(locationSalaryData.value.reduce((sum, d) => sum + d.P75, 0) / count)
  const avgP90 = Math.round(locationSalaryData.value.reduce((sum, d) => sum + d.P90, 0) / count)

  return { P10: avgP10, P25: avgP25, P50: avgP50, P75: avgP75, P90: avgP90, count }
})

// Location display name for the summary paragraph (uses displayed values)
const locationDisplayName = computed(() => {
  if (displayedMetro.value) return displayedMetro.value
  if (displayedStateName.value) return displayedStateName.value
  return 'the United States'
})

// Current year for page title
const currentYear = new Date().getFullYear()

// Dynamic H1 title based on role and location
const pageTitle = computed(() => {
  if (displayedRole.value && (displayedMetro.value || displayedStateName.value)) {
    // Role + Location: "Cashier Salary in Birmingham, AL (2026)"
    return `${displayedRole.value} Salary in ${locationDisplayName.value} (${currentYear})`
  }
  if (displayedRole.value) {
    // Role only: "Cashier Salary (2026)"
    return `${displayedRole.value} Salary (${currentYear})`
  }
  if (displayedMetro.value || displayedStateName.value) {
    // Location only: "Salary Calculator - Birmingham, AL (2026)"
    return `Salary Calculator - ${locationDisplayName.value} (${currentYear})`
  }
  // Default
  return 'Salary Calculator'
})

// Deduplicate salary data by metropolitan area, averaging values for duplicates
const uniqueSalaryData = computed(() => {
  const metroMap = new Map<string, { data: SalaryData; count: number; totals: { P10: number; P25: number; P50: number; P75: number; P90: number } }>()

  salaryData.value.forEach(item => {
    const metro = item.metropolitanArea
    if (!metro) return // Skip items without metropolitan area
    if (metroMap.has(metro)) {
      const existing = metroMap.get(metro)!
      existing.count++
      existing.totals.P10 += item.P10
      existing.totals.P25 += item.P25
      existing.totals.P50 += item.P50
      existing.totals.P75 += item.P75
      existing.totals.P90 += item.P90
    } else {
      metroMap.set(metro, {
        data: item,
        count: 1,
        totals: { P10: item.P10, P25: item.P25, P50: item.P50, P75: item.P75, P90: item.P90 }
      })
    }
  })

  return Array.from(metroMap.values()).map(({ data, count, totals }) => ({
    ...data,
    P10: Math.round(totals.P10 / count),
    P25: Math.round(totals.P25 / count),
    P50: Math.round(totals.P50 / count),
    P75: Math.round(totals.P75 / count),
    P90: Math.round(totals.P90 / count)
  }))
})

const aggregatedSalary = computed(() => {
  if (!salaryData.value.length) return null

  const count = salaryData.value.length
  const avgP10 = Math.round(salaryData.value.reduce((sum, d) => sum + d.P10, 0) / count)
  const avgP25 = Math.round(salaryData.value.reduce((sum, d) => sum + d.P25, 0) / count)
  const avgP50 = Math.round(salaryData.value.reduce((sum, d) => sum + d.P50, 0) / count)
  const avgP75 = Math.round(salaryData.value.reduce((sum, d) => sum + d.P75, 0) / count)
  const avgP90 = Math.round(salaryData.value.reduce((sum, d) => sum + d.P90, 0) / count)

  return { P10: avgP10, P25: avgP25, P50: avgP50, P75: avgP75, P90: avgP90, count }
})

// Aggregated national salary (US-wide for the role)
const aggregatedNationalSalary = computed(() => {
  if (!nationalSalaryData.value.length) return null

  const count = nationalSalaryData.value.length
  const avgP50 = Math.round(nationalSalaryData.value.reduce((sum, d) => sum + d.P50, 0) / count)

  return { P50: avgP50, count }
})

// Aggregated state salary (state-wide for the role)
const aggregatedStateSalary = computed(() => {
  if (!stateSalaryData.value.length) return null

  const count = stateSalaryData.value.length
  const avgP50 = Math.round(stateSalaryData.value.reduce((sum, d) => sum + d.P50, 0) / count)

  return { P50: avgP50, count }
})

// Outside click handler
const handleOutsideClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.state-dropdown-container')) {
    showStateDropdown.value = false
  }
  if (!target.closest('.metro-dropdown-container')) {
    showMetroDropdown.value = false
  }
  if (!target.closest('.role-dropdown-container')) {
    showRoleDropdown.value = false
  }
}

// Lifecycle - Client-side only event listeners
onMounted(() => {
  if (import.meta.client) {
    document.addEventListener('click', handleOutsideClick)
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    document.removeEventListener('click', handleOutsideClick)
  }
})

// Dynamic SEO based on displayed parameters (what's currently shown in results)
// Uses watchEffect to reactively update meta when data changes
watchEffect(() => {
  // Determine which salary data to use (role-specific or location-aggregated)
  const salary = displayedRole.value
    ? aggregatedSalary.value
    : aggregatedLocationSalary.value

  generateSalaryCalculatorPageMeta({
    role: displayedRole.value,
    location: displayedMetro.value || displayedStateName.value,
    stateName: displayedStateName.value,
    stateCode: displayedState.value,
    metro: displayedMetro.value,
    salary,
    faqItems: faqItems.value,
    canonicalPath: route.path,
    indexable: indexableData.value?.indexable ?? false
  })
})
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header Section -->
    <div class="bg-white py-6 sm:py-8 lg:py-10 pb-16 relative z-20">
      <div class="absolute right-0 top-0 bottom-0 opacity-10 lg:opacity-20 pointer-events-none hidden lg:flex items-center justify-end pr-4 overflow-hidden" style="width: 40%; max-width: 400px;">
        <img
          src="/img/svg/employers-homepage-animation.svg"
          alt=""
          width="400"
          height="256"
          loading="lazy"
          fetchpriority="low"
          decoding="async"
          class="w-full h-auto max-h-full object-contain"
        />
      </div>
      <Container class="relative z-30">
        <div>
          <div class="mb-6">
            <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              {{ pageTitle }}
            </h1>
            <p class="text-sm sm:text-base lg:text-lg text-gray-600">
              Find Salaries by Job Title, State & City
            </p>
          </div>

          <!-- Calculator Form -->
          <form class="relative z-40" @submit.prevent>
            <div class="gap-2 grid grid-cols-1 lg:grid-cols-4">
              <div class="lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-1">
                <!-- Role Selector (First) -->
                <div class="role-dropdown-container relative bg-white rounded-full sm:rounded-l-full sm:rounded-r-none shadow-md">
                  <Icon
                    name="i-heroicons-briefcase"
                    class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5"
                    style="color: #5F0CE2"
                  />
                  <input
                    v-model="roleSearchQuery"
                    type="text"
                    placeholder="Search Job Title"
                    class="w-full pl-10 sm:pl-11 pr-8 py-3 text-sm sm:text-base text-gray-900 placeholder-gray-500 bg-transparent border-none outline-none rounded-full sm:rounded-l-full sm:rounded-r-none"
                    @input="handleRoleSearch"
                    @focus="handleRoleFocus"
                  />
                  <div v-if="isLoadingRoles" class="absolute right-3 top-1/2 -translate-y-1/2">
                    <Icon name="i-heroicons-arrow-path" class="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                  <Icon
                    v-else-if="selectedRole"
                    name="i-heroicons-x-mark"
                    class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer"
                    @click.stop="clearRole"
                  />

                  <!-- Role Dropdown -->
                  <div
                    v-if="showRoleDropdown && searchResults.length > 0"
                    class="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
                  >
                    <button
                      v-for="job in searchResults"
                      :key="job.title"
                      type="button"
                      class="w-full px-4 py-2 text-left hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                      @click="selectRole(job)"
                    >
                      <p class="text-sm font-medium text-gray-900">{{ job.title }}</p>
                    </button>
                  </div>
                </div>

                <!-- State Selector (Middle) -->
                <div class="state-dropdown-container relative bg-white shadow-md">
                  <Icon
                    name="i-heroicons-map-pin"
                    class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5"
                    style="color: #5F0CE2"
                  />
                  <button
                    type="button"
                    class="w-full pl-10 sm:pl-11 pr-8 py-3 text-sm sm:text-base text-left text-gray-900 bg-transparent border-none outline-none cursor-pointer"
                    @click="showStateDropdown = !showStateDropdown"
                  >
                    <span :class="selectedState ? 'text-gray-900' : 'text-gray-500'">
                      {{ selectedStateName || 'Select State' }}
                    </span>
                  </button>
                  <Icon
                    v-if="selectedState"
                    name="i-heroicons-x-mark"
                    class="absolute right-8 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer"
                    @click.stop="clearState"
                  />
                  <Icon
                    name="i-heroicons-chevron-down"
                    class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  />

                  <!-- State Dropdown -->
                  <div
                    v-if="showStateDropdown"
                    class="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
                  >
                    <button
                      v-for="jurisdiction in jurisdictions"
                      :key="jurisdiction.code"
                      type="button"
                      class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-gray-900"
                      @click="selectState(jurisdiction)"
                    >
                      {{ jurisdiction.name }}
                    </button>
                  </div>
                </div>

                <!-- City Selector (Last) -->
                <div class="metro-dropdown-container relative bg-white rounded-full sm:rounded-r-full sm:rounded-l-none shadow-md">
                  <Icon
                    name="i-heroicons-building-office-2"
                    class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5"
                    style="color: #5F0CE2"
                  />
                  <input
                    v-model="metroSearchQuery"
                    type="text"
                    :placeholder="selectedState ? 'Search City' : 'Select State first'"
                    :disabled="!selectedState"
                    class="w-full pl-10 sm:pl-11 pr-8 py-3 text-sm sm:text-base text-gray-900 placeholder-gray-500 bg-transparent border-none outline-none disabled:opacity-50 disabled:cursor-not-allowed rounded-full sm:rounded-r-full sm:rounded-l-none"
                    @input="handleCitySearch"
                    @focus="handleMetroFocus"
                  />
                  <div v-if="isLoadingCities" class="absolute right-3 top-1/2 -translate-y-1/2">
                    <Icon name="i-heroicons-arrow-path" class="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                  <Icon
                    v-else-if="selectedMetro"
                    name="i-heroicons-x-mark"
                    class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer"
                    @click.stop="clearMetro"
                  />

                  <!-- City Dropdown -->
                  <div
                    v-if="showMetroDropdown && selectedState && filteredCities.length > 0"
                    class="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
                  >
                    <button
                      v-for="city in filteredCities"
                      :key="city.label"
                      type="button"
                      class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-gray-900"
                      @click="selectMetro(city)"
                    >
                      {{ city.label }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Calculate Button -->
              <div class="lg:col-span-1 flex items-stretch">
                <Button
                  type="button"
                  block
                  size="lg"
                  :disabled="!selectedRole && !selectedState && !selectedMetro"
                  :loading="isLoadingSalary || isLoadingTopJobs"
                  class="w-full h-full rounded-full flex items-center justify-center gap-2"
                  style="background-color: #5F0CE2"
                  @click="handleCalculate"
                >
                  <Icon name="i-heroicons-calculator" class="w-5 h-5" />
                  <span>Calculate</span>
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Container>
    </div>

    <!-- Results Section -->
    <Container class="py-6 sm:py-8 lg:py-12">
      <div class="space-y-6 lg:space-y-8">
          <!-- Loading State -->
          <Card v-if="isLoadingSalary || isLoadingTopJobs || isLoadingLocationSalary" class="text-center py-12">
            <div class="flex flex-col items-center gap-4">
              <Icon name="i-heroicons-arrow-path" class="w-12 h-12 animate-spin" style="color: #5F0CE2" />
              <p class="text-gray-600">{{ isLoadingSalary ? 'Calculating salaries...' : 'Finding salary data...' }}</p>
            </div>
          </Card>

          <!-- Empty State -->
          <template v-if="!displayedRole && !hasLocationOnlyResults && !isLoadingSalary && !isLoadingTopJobs && !isLoadingLocationSalary">
            <Card>
              <template #header>
                <div class="flex items-center gap-2">
                  <Icon name="i-heroicons-light-bulb" class="w-5 h-5" style="color: #5F0CE2" />
                  <h2 class="font-semibold">How to Use</h2>
                </div>
              </template>
              <div class="space-y-4">
                <p class="text-sm text-gray-600">
                  Search for any job title to see salary ranges and percentiles. Optionally filter by state and city for location-specific data.
                </p>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div class="p-4 bg-gray-50 rounded-lg">
                    <div class="flex items-center gap-2 mb-2">
                      <div class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style="background-color: #5F0CE2">1</div>
                      <span class="font-medium text-gray-900">Search Role</span>
                    </div>
                    <p class="text-xs text-gray-500">Start typing a job title to search</p>
                  </div>
                  <div class="p-4 bg-gray-50 rounded-lg">
                    <div class="flex items-center gap-2 mb-2">
                      <div class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style="background-color: #5F0CE2">2</div>
                      <span class="font-medium text-gray-900">Select Location</span>
                    </div>
                    <p class="text-xs text-gray-500">Optionally filter by state and city</p>
                  </div>
                  <div class="p-4 bg-gray-50 rounded-lg">
                    <div class="flex items-center gap-2 mb-2">
                      <div class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style="background-color: #5F0CE2">3</div>
                      <span class="font-medium text-gray-900">View Results</span>
                    </div>
                    <p class="text-xs text-gray-500">See salary ranges and percentiles</p>
                  </div>
                </div>
              </div>
            </Card>

            <!-- Popular Job Searches -->
            <Card>
              <template #header>
                <div class="flex items-center gap-2">
                  <Icon name="i-heroicons-fire" class="w-5 h-5" style="color: #5F0CE2" />
                  <h2 class="font-semibold">Popular Searches</h2>
                </div>
              </template>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <button
                  v-for="job in ['Software Developer', 'Registered Nurse', 'Marketing Manager', 'Accountant', 'Data Analyst', 'Project Manager']"
                  :key="job"
                  class="p-3 rounded-lg bg-gray-50 hover:bg-purple-50 transition-all text-left group"
                  @click="roleSearchQuery = job; handleRoleSearch()"
                >
                  <div class="text-sm font-medium text-gray-900 group-hover:text-purple-600">{{ job }}</div>
                </button>
              </div>
            </Card>

            <!-- Top Paying Jobs (when location selected but no role) -->
            <Card v-if="uniqueTopPayingJobs.length > 0">
              <template #header>
                <div class="flex items-center gap-2">
                  <Icon name="i-heroicons-trophy" class="w-5 h-5" style="color: #5F0CE2" />
                  <h2 class="font-semibold">{{ relatedJobsTitle }}</h2>
                </div>
              </template>
              <div class="prose prose-sm max-w-none mb-6">
                <p class="text-gray-600 leading-relaxed">
                  In <strong class="text-gray-900">{{ locationDisplayName }}</strong>, several career paths offer excellent compensation. Here are the top paying positions available in this area, based on median annual salaries.
                </p>
              </div>
              <div class="space-y-4">
                <div
                  v-for="(job, index) in uniqueTopPayingJobs"
                  :key="job.jobTitle"
                  class="p-4 bg-gray-50 rounded-lg border border-gray-100"
                >
                  <div class="flex items-start justify-between gap-4">
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <span
                          class="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white"
                          style="background-color: #5F0CE2"
                        >
                          {{ index + 1 }}
                        </span>
                        <h3 class="font-semibold">
                          <a
                            v-if="isJobIndexed(job.jobTitle)"
                            :href="buildRoleUrl(job.jobTitle)"
                            class="text-gray-900 hover:text-purple-600 hover:underline transition-colors"
                          >
                            {{ job.jobTitle }}
                          </a>
                          <span v-else class="text-gray-900">{{ job.jobTitle }}</span>
                        </h3>
                      </div>
                      <p class="text-sm text-gray-600 mt-2">
                        Professionals in this role earn an average of <strong class="text-gray-900">{{ formatCurrency(job.avgSalary) }}</strong> annually. Compensation ranges from {{ formatCurrency(job.P10) }} for entry-level positions to {{ formatCurrency(job.P90) }} for experienced professionals.
                      </p>
                    </div>
                    <div class="text-right shrink-0">
                      <p class="text-lg font-bold" style="color: #5F0CE2">{{ formatCurrency(job.avgSalary) }}</p>
                      <p class="text-xs text-gray-500">median salary</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="prose prose-sm max-w-none mt-6">
                <p class="text-gray-600 leading-relaxed">
                  These positions represent some of the most lucrative opportunities in <strong class="text-gray-900">{{ locationDisplayName }}</strong>. Job seekers should consider their qualifications, experience level, and career goals when exploring these high-paying roles.
                </p>
              </div>
            </Card>
          </template>

          <!-- Location Only Results (Median Salary + Top Paying Jobs when no role selected) -->
          <template v-if="hasLocationOnlyResults && !isLoadingTopJobs && !isLoadingLocationSalary">
            <!-- Location Median Salary Card -->
            <Card v-if="aggregatedLocationSalary">
              <template #header>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <Icon name="i-heroicons-currency-dollar" class="w-5 h-5" style="color: #5F0CE2" />
                    <h2 class="font-semibold">Salaries in {{ locationDisplayName }}</h2>
                  </div>
                  <span class="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                    {{ aggregatedLocationSalary.count }} data points
                  </span>
                </div>
              </template>

              <div class="space-y-6">
                <!-- Median Salary Highlight -->
                <div class="text-center py-6 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl">
                  <p class="text-sm text-gray-600 mb-1">Median Annual Salary</p>
                  <p class="text-4xl sm:text-5xl font-bold" style="color: #5F0CE2">{{ formatCurrency(aggregatedLocationSalary.P50) }}</p>
                  <p class="text-sm text-gray-500 mt-2">{{ formatCurrency(Math.round(aggregatedLocationSalary.P50 / 12)) }}/month</p>
                </div>

                <!-- Salary Range Visualization -->
                <div class="space-y-3">
                  <h3 class="text-sm font-medium text-gray-700">Salary Distribution</h3>
                  <div class="flex justify-between text-xs text-gray-500">
                    <span>{{ formatCurrency(aggregatedLocationSalary.P10) }}</span>
                    <span class="font-medium" style="color: #5F0CE2">{{ formatCurrency(aggregatedLocationSalary.P50) }}</span>
                    <span>{{ formatCurrency(aggregatedLocationSalary.P90) }}</span>
                  </div>
                  <div class="h-4 bg-gray-200 rounded-full overflow-hidden">
                    <div class="h-full rounded-full" style="background: linear-gradient(to right, #ef4444, #f59e0b, #5F0CE2, #10b981)" />
                  </div>
                  <div class="flex justify-between text-[10px] text-gray-400">
                    <span>10th %ile</span>
                    <span>25th</span>
                    <span>50th (Median)</span>
                    <span>75th</span>
                    <span>90th %ile</span>
                  </div>
                </div>

                <!-- Salary Comparison Cards (National, State, City) -->
                <div class="grid grid-cols-3 gap-3">
                  <!-- National (United States) -->
                  <div class="text-center p-4 bg-green-50 rounded-lg border border-green-100">
                    <p class="text-sm text-gray-600 mb-1">United States</p>
                    <p class="text-base sm:text-lg font-semibold text-gray-900">{{ aggregatedNationalSalary ? formatCurrency(aggregatedNationalSalary.P50) : '—' }}</p>
                  </div>
                  <!-- State (use aggregatedLocationSalary when state-only, no metro) -->
                  <div class="text-center p-4 rounded-lg border" :class="displayedMetro ? 'bg-green-50 border-green-100' : 'bg-blue-100 border-blue-200'">
                    <p class="text-sm text-gray-600 mb-1">{{ displayedStateName || 'State' }}</p>
                    <p class="text-base sm:text-lg font-semibold text-gray-900">{{ displayedMetro ? (aggregatedStateSalary ? formatCurrency(aggregatedStateSalary.P50) : '—') : (aggregatedLocationSalary ? formatCurrency(aggregatedLocationSalary.P50) : '—') }}</p>
                  </div>
                  <!-- City/Metro -->
                  <div class="text-center p-4 rounded-lg border" :class="displayedMetro ? 'bg-blue-100 border-blue-200' : 'bg-gray-50 border-gray-200'">
                    <p class="text-sm text-gray-600 mb-1">{{ displayedMetro || 'City' }}</p>
                    <p class="text-base sm:text-lg font-semibold text-gray-900">{{ displayedMetro && aggregatedLocationSalary ? formatCurrency(aggregatedLocationSalary.P50) : '—' }}</p>
                  </div>
                </div>

                <!-- Location Salary Overview -->
                <div class="prose prose-sm max-w-none pt-4 border-t border-gray-200">
                  <p class="text-gray-600 leading-relaxed">
                    The median annual salary in <strong class="text-gray-900">{{ locationDisplayName }}</strong> is <strong class="text-gray-900">{{ formatCurrency(aggregatedLocationSalary.P50) }}</strong>. Salaries typically range from <strong class="text-gray-900">{{ formatCurrency(aggregatedLocationSalary.P10) }}</strong> for entry-level positions to <strong class="text-gray-900">{{ formatCurrency(aggregatedLocationSalary.P90) }}</strong> for top earners, depending on the role, experience, and industry.
                  </p>
                </div>
              </div>
            </Card>

            <!-- Top Paying Jobs Card -->
            <Card v-if="uniqueTopPayingJobs.length > 0">
              <template #header>
                <div class="flex items-center gap-2">
                  <Icon name="i-heroicons-trophy" class="w-5 h-5" style="color: #5F0CE2" />
                  <h2 class="font-semibold">{{ relatedJobsTitle }}</h2>
                </div>
              </template>
              <div class="prose prose-sm max-w-none mb-6">
                <p class="text-gray-600 leading-relaxed">
                  In <strong class="text-gray-900">{{ locationDisplayName }}</strong>, several career paths offer excellent compensation. Here are the top paying positions available in this area, based on median annual salaries.
                </p>
              </div>
              <div class="space-y-4">
                <div
                  v-for="(job, index) in uniqueTopPayingJobs"
                  :key="job.jobTitle"
                  class="p-4 bg-gray-50 rounded-lg border border-gray-100"
                >
                  <div class="flex items-start justify-between gap-4">
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <span
                          class="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white"
                          style="background-color: #5F0CE2"
                        >
                          {{ index + 1 }}
                        </span>
                        <h3 class="font-semibold">
                          <a
                            v-if="isJobIndexed(job.jobTitle)"
                            :href="buildRoleUrl(job.jobTitle)"
                            class="text-gray-900 hover:text-purple-600 hover:underline transition-colors"
                          >
                            {{ job.jobTitle }}
                          </a>
                          <span v-else class="text-gray-900">{{ job.jobTitle }}</span>
                        </h3>
                      </div>
                      <p class="text-sm text-gray-600 mt-2">
                        Professionals in this role earn an average of <strong class="text-gray-900">{{ formatCurrency(job.avgSalary) }}</strong> annually. Compensation ranges from {{ formatCurrency(job.P10) }} for entry-level positions to {{ formatCurrency(job.P90) }} for experienced professionals.
                      </p>
                    </div>
                    <div class="text-right shrink-0">
                      <p class="text-lg font-bold" style="color: #5F0CE2">{{ formatCurrency(job.avgSalary) }}</p>
                      <p class="text-xs text-gray-500">median salary</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="prose prose-sm max-w-none mt-6">
                <p class="text-gray-600 leading-relaxed">
                  These positions represent some of the most lucrative opportunities in <strong class="text-gray-900">{{ locationDisplayName }}</strong>. Job seekers should consider their qualifications, experience level, and career goals when exploring these high-paying roles.
                </p>
              </div>
            </Card>
          </template>

          <!-- Results Display -->
          <template v-if="hasResults && !isLoadingSalary">
            <!-- Main Salary Card -->
            <Card>
              <template #header>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <Icon name="i-heroicons-currency-dollar" class="w-5 h-5" style="color: #5F0CE2" />
                    <h2 class="font-semibold">{{ displayedRole }}</h2>
                  </div>
                  <span class="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                    {{ aggregatedSalary?.count }} data points
                  </span>
                </div>
              </template>

              <div v-if="aggregatedSalary" class="space-y-6">
                <!-- Location Info -->
                <div v-if="displayedState || displayedMetro" class="flex items-center gap-2 text-sm text-gray-600">
                  <Icon name="i-heroicons-map-pin" class="w-4 h-4" />
                  <span>{{ displayedMetro || displayedStateName || 'United States' }}</span>
                </div>

                <!-- Median Salary Highlight -->
                <div class="text-center py-6 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl">
                  <p class="text-sm text-gray-600 mb-1">Median Annual Salary</p>
                  <p class="text-4xl sm:text-5xl font-bold" style="color: #5F0CE2">{{ formatCurrency(aggregatedSalary.P50) }}</p>
                  <p class="text-sm text-gray-500 mt-2">{{ formatCurrency(Math.round(aggregatedSalary.P50 / 12)) }}/month</p>
                </div>

                <!-- Salary Range Visualization -->
                <div class="space-y-3">
                  <h3 class="text-sm font-medium text-gray-700">Salary Distribution</h3>
                  <div class="flex justify-between text-xs text-gray-500">
                    <span>{{ formatCurrency(aggregatedSalary.P10) }}</span>
                    <span class="font-medium" style="color: #5F0CE2">{{ formatCurrency(aggregatedSalary.P50) }}</span>
                    <span>{{ formatCurrency(aggregatedSalary.P90) }}</span>
                  </div>
                  <div class="h-4 bg-gray-200 rounded-full overflow-hidden">
                    <div class="h-full rounded-full" style="background: linear-gradient(to right, #ef4444, #f59e0b, #5F0CE2, #10b981)" />
                  </div>
                  <div class="flex justify-between text-[10px] text-gray-400">
                    <span>10th %ile</span>
                    <span>25th</span>
                    <span>50th (Median)</span>
                    <span>75th</span>
                    <span>90th %ile</span>
                  </div>
                </div>

                <!-- Salary Comparison Cards (National, State, City) -->
                <div class="grid grid-cols-3 gap-3">
                  <!-- National (United States) -->
                  <div class="text-center p-4 rounded-lg border" :class="(displayedState || displayedMetro) ? 'bg-green-50 border-green-100' : 'bg-blue-100 border-blue-200'">
                    <p class="text-sm text-gray-600 mb-1">United States</p>
                    <p class="text-base sm:text-lg font-semibold text-gray-900">{{ (displayedState || displayedMetro) ? (aggregatedNationalSalary ? formatCurrency(aggregatedNationalSalary.P50) : '—') : (aggregatedSalary ? formatCurrency(aggregatedSalary.P50) : '—') }}</p>
                  </div>
                  <!-- State (use aggregatedSalary when state-only with role, no metro) -->
                  <div class="text-center p-4 rounded-lg border" :class="displayedMetro ? 'bg-green-50 border-green-100' : (displayedState ? 'bg-blue-100 border-blue-200' : 'bg-gray-50 border-gray-200')">
                    <p class="text-sm text-gray-600 mb-1">{{ displayedStateName || 'State' }}</p>
                    <p class="text-base sm:text-lg font-semibold text-gray-900">{{ displayedMetro ? (aggregatedStateSalary ? formatCurrency(aggregatedStateSalary.P50) : '—') : (displayedState && aggregatedSalary ? formatCurrency(aggregatedSalary.P50) : '—') }}</p>
                  </div>
                  <!-- City/Metro -->
                  <div class="text-center p-4 rounded-lg border" :class="displayedMetro ? 'bg-blue-100 border-blue-200' : 'bg-gray-50 border-gray-200'">
                    <p class="text-sm text-gray-600 mb-1">{{ displayedMetro || 'City' }}</p>
                    <p class="text-base sm:text-lg font-semibold text-gray-900">{{ displayedMetro && aggregatedSalary ? formatCurrency(aggregatedSalary.P50) : '—' }}</p>
                  </div>
                </div>

              </div>
            </Card>

            <!-- Salary Overview Paragraph -->
            <Card v-if="aggregatedSalary">
              <template #header>
                <div class="flex items-center gap-2">
                  <Icon name="i-heroicons-document-text" class="w-5 h-5" style="color: #5F0CE2" />
                  <h2 class="font-semibold">Salary Overview</h2>
                </div>
              </template>
              <div class="prose prose-sm max-w-none">
                <p class="text-gray-600 leading-relaxed">
                  Professionals working as a <strong class="text-gray-900">{{ displayedRole }}</strong> in <strong class="text-gray-900">{{ locationDisplayName }}</strong> can expect a range of salary opportunities. The median annual salary is <strong class="text-gray-900">{{ formatCurrency(aggregatedSalary.P50) }}</strong>. This figure reflects the compensation landscape for this role in the area. Earnings typically range from <strong class="text-gray-900">{{ formatCurrency(aggregatedSalary.P10) }}</strong> at the entry level to <strong class="text-gray-900">{{ formatCurrency(aggregatedSalary.P90) }}</strong> for top earners, depending on experience, skills, and employer.
                </p>
                <p class="text-gray-600 leading-relaxed mt-3">
                  Job seekers pursuing <strong class="text-gray-900">{{ displayedRole }}</strong> positions should consider factors such as industry sector, company size, and local cost of living when evaluating opportunities. Those with specialized certifications or advanced experience may command salaries at the higher end of this range.
                </p>
              </div>
            </Card>

            <!-- Salary by Metro Area Table -->
            <Card v-if="uniqueSalaryData.length > 1">
              <template #header>
                <div class="flex items-center gap-2">
                  <Icon name="i-heroicons-table-cells" class="w-5 h-5" style="color: #5F0CE2" />
                  <h2 class="font-semibold">Salary by Location</h2>
                </div>
              </template>
              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="border-b border-gray-200">
                      <th class="text-left py-3 px-2 font-medium text-gray-700">Location</th>
                      <th class="text-right py-3 px-2 font-medium text-gray-700">P25</th>
                      <th class="text-right py-3 px-2 font-medium text-gray-700">Median</th>
                      <th class="text-right py-3 px-2 font-medium text-gray-700">P75</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="data in uniqueSalaryData"
                      :key="data.metropolitanArea"
                      class="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td class="py-3 px-2">
                        <a
                          :href="buildMetroUrl(data.metropolitanArea, displayedRole)"
                          class="text-gray-900 hover:text-purple-600 hover:underline transition-colors cursor-pointer"
                        >
                          {{ data.metropolitanArea }}
                        </a>
                      </td>
                      <td class="text-right py-3 px-2 text-gray-600">{{ formatCurrency(data.P25) }}</td>
                      <td class="text-right py-3 px-2 font-semibold" style="color: #5F0CE2">{{ formatCurrency(data.P50) }}</td>
                      <td class="text-right py-3 px-2 text-gray-600">{{ formatCurrency(data.P75) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>

            <!-- Top Paying Jobs Section (only for location-only results, not when role selected) -->
            <Card v-if="uniqueTopPayingJobs.length > 0 && !displayedSocCode">
              <template #header>
                <div class="flex items-center gap-2">
                  <Icon name="i-heroicons-trophy" class="w-5 h-5" style="color: #5F0CE2" />
                  <h2 class="font-semibold">{{ relatedJobsTitle }}</h2>
                </div>
              </template>
              <div class="prose prose-sm max-w-none mb-6">
                <p class="text-gray-600 leading-relaxed">
                  In <strong class="text-gray-900">{{ locationDisplayName }}</strong>, several career paths offer excellent compensation. Here are the top paying positions available in this area, based on median annual salaries.
                </p>
              </div>
              <div class="space-y-4">
                <div
                  v-for="(job, index) in uniqueTopPayingJobs"
                  :key="job.jobTitle"
                  class="p-4 bg-gray-50 rounded-lg border border-gray-100"
                >
                  <div class="flex items-start justify-between gap-4">
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <span
                          class="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white"
                          style="background-color: #5F0CE2"
                        >
                          {{ index + 1 }}
                        </span>
                        <h3 class="font-semibold">
                          <a
                            v-if="isJobIndexed(job.jobTitle)"
                            :href="buildRoleUrl(job.jobTitle)"
                            class="text-gray-900 hover:text-purple-600 hover:underline transition-colors"
                          >
                            {{ job.jobTitle }}
                          </a>
                          <span v-else class="text-gray-900">{{ job.jobTitle }}</span>
                        </h3>
                      </div>
                      <p class="text-sm text-gray-600 mt-2">
                        Professionals in this role earn an average of <strong class="text-gray-900">{{ formatCurrency(job.avgSalary) }}</strong> annually. Compensation ranges from {{ formatCurrency(job.P10) }} for entry-level positions to {{ formatCurrency(job.P90) }} for experienced professionals.
                      </p>
                    </div>
                    <div class="text-right shrink-0">
                      <p class="text-lg font-bold" style="color: #5F0CE2">{{ formatCurrency(job.avgSalary) }}</p>
                      <p class="text-xs text-gray-500">median salary</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="prose prose-sm max-w-none mt-6">
                <p class="text-gray-600 leading-relaxed">
                  These positions represent some of the most lucrative opportunities in <strong class="text-gray-900">{{ locationDisplayName }}</strong>. Job seekers should consider their qualifications, experience level, and career goals when exploring these high-paying roles.
                </p>
              </div>
            </Card>

          </template>

          <!-- Related Jobs Section (shows when role is selected, independent of salary data) -->
          <Card v-if="displayedSocCode && uniqueTopPayingJobs.length > 0 && !isLoadingTopJobs">
            <template #header>
              <div class="flex items-center gap-2">
                <Icon name="i-heroicons-users" class="w-5 h-5" style="color: #5F0CE2" />
                <h2 class="font-semibold">{{ relatedJobsTitle }}</h2>
              </div>
            </template>
            <div class="prose prose-sm max-w-none mb-6">
              <p class="text-gray-600 leading-relaxed">
                These are jobs in the same occupation category as <strong class="text-gray-900">{{ displayedRole }}</strong>{{ locationDisplayName !== 'the United States' ? ' in ' + locationDisplayName : '' }}. Compare median salaries across related roles.
              </p>
            </div>
            <div class="space-y-3">
              <div
                v-for="(job, index) in uniqueTopPayingJobs"
                :key="job.jobTitle"
                class="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100"
              >
                <div class="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-white rounded-lg overflow-hidden">
                  <img
                    v-if="job.logo"
                    :src="job.logo"
                    :alt="job.company_name || job.jobTitle"
                    class="w-full h-full object-contain p-1"
                    loading="lazy"
                  />
                  <span
                    v-else
                    class="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white"
                    style="background-color: #5F0CE2"
                  >
                    {{ index + 1 }}
                  </span>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="font-semibold text-sm">
                    <a
                      v-if="isJobIndexed(job.jobTitle)"
                      :href="buildRoleUrl(job.jobTitle)"
                      class="text-gray-900 hover:text-purple-600 hover:underline transition-colors"
                    >
                      {{ job.jobTitle }}
                    </a>
                    <span v-else class="text-gray-900">{{ job.jobTitle }}</span>
                  </h3>
                  <p class="text-xs text-gray-500 mt-1">
                    Range: {{ formatCurrency(job.P10) }} - {{ formatCurrency(job.P90) }}
                  </p>
                </div>
                <div class="text-right shrink-0">
                  <p class="text-lg font-bold" style="color: #5F0CE2">{{ formatCurrency(job.avgSalary) }}</p>
                  <p class="text-xs text-gray-500">median</p>
                </div>
              </div>
            </div>
          </Card>

          <!-- Related Salary Calculations - Link to Tax Calculator -->
          <Card v-if="displayedStateSlug">
            <template #header>
              <div class="flex items-center gap-2">
                <Icon name="i-heroicons-calculator" class="w-5 h-5" style="color: #5F0CE2" />
                <h2 class="font-semibold">Related Salary Calculations in {{ displayedStateName }}</h2>
              </div>
            </template>
            <p class="text-sm text-gray-600 mb-4">
              Curious about your take-home pay? Use our tax calculator to see how much you'd actually earn after federal and {{ displayedStateName }} state taxes.
            </p>
            <TaxRelatedSalaryLinks
              :state-slug="displayedStateSlug"
              :current-salary="currentMedianSalary"
              current-period="annually"
              country-code="US"
            />
          </Card>

          <!-- No Results - Role search -->
          <Card v-if="displayedRole && !isLoadingSalary && salaryData.length === 0 && uniqueTopPayingJobs.length === 0">
            <div class="text-center py-8">
              <Icon name="i-heroicons-magnifying-glass" class="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 class="text-lg font-medium text-gray-900 mb-2">No Data Found</h3>
              <p class="text-sm text-gray-600">
                We couldn't find salary data for "{{ displayedRole }}"
                <span v-if="displayedState"> in {{ displayedStateName }}</span>.
                Try a different job title or remove location filters.
              </p>
            </div>
          </Card>

          <!-- No Results - Location only search -->
          <Card v-if="!displayedRole && (displayedState || displayedMetro) && !isLoadingTopJobs && !isLoadingLocationSalary && topPayingJobs.length === 0 && locationSalaryData.length === 0">
            <div class="text-center py-8">
              <Icon name="i-heroicons-magnifying-glass" class="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 class="text-lg font-medium text-gray-900 mb-2">No Data Found</h3>
              <p class="text-sm text-gray-600">
                We couldn't find salary data for {{ locationDisplayName }}.
                Try a different location or search for a specific job title.
              </p>
            </div>
          </Card>

          <!-- Jobs Hiring Today Section -->
          <div v-if="locationJobs.length > 0 && !isLoadingLocationJobs">
            <div class="flex items-center gap-2 mb-4">
              <Icon name="i-heroicons-briefcase" class="w-5 h-5" style="color: #5F0CE2" />
              <h2 class="font-semibold text-gray-900">Jobs Hiring Today in {{ locationDisplayName }}</h2>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NuxtLink
                v-for="job in locationJobs"
                :key="job.job_id"
                :to="`/company/${job.company_permalink}/viewJob/${job.job_id}`"
                class="group bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-xl hover:border-purple-200 transition-all duration-300 transform hover:-translate-y-1"
              >
                <div class="flex items-start space-x-4 mb-4">
                  <div class="relative flex-shrink-0">
                    <div class="w-14 h-14 flex items-center justify-center bg-gray-50 rounded-xl overflow-hidden">
                      <img
                        v-if="job.logo"
                        :src="job.logo"
                        :alt="`${job.company_name} logo`"
                        class="w-full h-full object-contain p-1"
                        loading="lazy"
                      />
                      <span
                        v-else
                        class="text-2xl font-bold text-gray-400"
                      >
                        {{ job.company_name?.charAt(0) || '?' }}
                      </span>
                    </div>
                    <div
                      class="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center"
                    >
                      <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-base font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-purple-600 transition-colors">
                      {{ job.title }}
                    </h3>
                    <p class="text-sm font-medium text-gray-600">
                      {{ job.company_name }}
                    </p>
                  </div>
                </div>
                <div class="space-y-2 mb-4">
                  <div class="flex items-center text-sm text-gray-600">
                    <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span class="truncate">{{ job.city }}, {{ job.state }}</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span
                      v-if="job.type"
                      class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                    >
                      {{ job.type }}
                    </span>
                    <span
                      v-if="formatJobSalary(job)"
                      class="text-sm font-semibold text-green-700"
                    >
                      {{ formatJobSalary(job) }}
                    </span>
                  </div>
                </div>
                <div class="pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span class="text-xs text-gray-500">
                    Posted {{ formatTimeAgo(job.published_at) }}
                  </span>
                  <div class="flex items-center text-sm font-medium group-hover:translate-x-1 transition-transform" style="color: #5F0CE2">
                    View Job
                    <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </NuxtLink>
            </div>
          </div>

          <!-- FAQ Section -->
          <Card v-if="faqItems.length > 0">
            <template #header>
              <div class="flex items-center gap-2">
                <Icon name="i-heroicons-question-mark-circle" class="w-5 h-5" style="color: #5F0CE2" />
                <h2 class="font-semibold">Frequently Asked Questions</h2>
              </div>
            </template>
            <div class="space-y-4">
              <details
                v-for="(item, index) in faqItems"
                :key="index"
                class="group border border-gray-200 rounded-lg overflow-hidden"
              >
                <summary class="flex items-center justify-between cursor-pointer p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                  <span class="font-medium text-gray-900 pr-4">{{ item.question }}</span>
                  <Icon
                    name="i-heroicons-chevron-down"
                    class="w-5 h-5 text-gray-500 shrink-0 transition-transform group-open:rotate-180"
                  />
                </summary>
                <div class="p-4 bg-white">
                  <p class="text-sm text-gray-600 leading-relaxed">{{ item.answer }}</p>
                </div>
              </details>
            </div>
          </Card>

          <!-- By States Section (only on root /salary-calculator) -->
          <Card v-if="!displayedRole && !displayedState && !displayedMetro">
            <template #header>
              <div class="flex items-center gap-2">
                <Icon name="i-heroicons-fire" class="w-5 h-5" style="color: #5F0CE2" />
                <h2 class="font-semibold">By States</h2>
              </div>
            </template>
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              <NuxtLink
                v-for="state in allStates"
                :key="state.code"
                :to="`/salary-calculator/${state.slug}`"
                class="px-3 py-2 text-sm text-gray-700 bg-gray-50 hover:bg-purple-50 hover:text-purple-700 rounded-lg transition-colors"
              >
                {{ state.name }}
              </NuxtLink>
            </div>
          </Card>

          <!-- Related Salary Pages (for state pages: show city+title links in that state) -->
          <TaxSalaryCalcRelatedPages
            v-if="displayedState && !displayedMetro && !displayedRole"
            :state="displayedState"
            :location-name="displayedStateName || ''"
            :is-state-page="true"
          />

          <!-- Related Salary Pages (for city+title pages: show other titles in that city) -->
          <TaxSalaryCalcRelatedPages
            v-if="displayedMetro && displayedRole"
            :city="citySlugForRelated"
            :location-name="displayedMetro || ''"
            :is-state-page="false"
            :current-slug="currentPageSlug"
          />

          <GeneralDisclaimer
            class="pt-4"
            disclaimer="Salary data is sourced from the Bureau of Labor Statistics and may not reflect the most recent market conditions. Actual salaries may vary based on experience, company size, and other factors."
            link-text="contact us"
            link-to="/employers/contact"
            post-text="if you have any questions."
          />
      </div>
    </Container>
  </div>
</template>
